import './css/bootstrap-datepicker3.min.css';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import './react-router-modal/react-router-modal.css';
import './App.css';
import './index.css';
import 'react-tooltip/dist/react-tooltip.css'
import React, { Component, type JSX } from 'react';
import { AppContext } from './context/app-context';
import { PendoProvider } from './context/pendo-context';
import SessionPoller from './services/session/SessionPoller';
import SecurSpaceRouter from './routing/SecurSpaceRouter';
import { isAuthenticatedAuthority, isSessionExpired, requestLoggedInAuthoritySource } from './services/session/session-requests';
import { getLandingRedirectPathForUser } from './routing/route-utils';
import Busy from './components/Busy';
import {
  LOGIN_PATH,
  LoginRedirectNestedPathnameBlockList,
  LoginRedirectPathnameBlockList,
  LoginRedirectedNestedPathnameExplicitlyAllowedList
} from './components/constants/securspace-constants';
import TagManager from 'react-gtm-module'
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import globalThemes from "./globalThemes";
import type { Account } from './types/Account';
import type { IAppState, IAppContext } from './interfaces';
import {GeocoderProvider} from "./context/geocoder-context";
import { SnackbarProvider } from './context/snackbar-context';
import {KeyValueObservable} from './util/observers/KeyValueObservable';
import {seedObservableMap, type ObservablePayloadType} from './components/constants/observable-constants';
import { FaroProvider } from './context/faro-context';
import { LDIdentityProvider } from './context/ld-identity-context';
import {NavigateFunction} from "react-router";
import { HelmetProvider } from 'react-helmet-async';
import { HeadClaimProvider, HeadDefaults } from './components/seo/HeadDefaults';

import { listenForVerification } from './services/session/verification-notifications';

const $ = window.$;

// superagent's own .timeout({response, deadline}) option is not always sufficient
// to guarantee a request settles — a degraded backend/proxy that accepts a
// connection but never completes the response can leave the underlying XHR
// pending indefinitely. This hard client-side timeout guarantees the promise
// always settles so the app's loading/busy state can never hang forever.
const INITIAL_AUTHORITY_TIMEOUT_MS = 20000;

const withHardTimeout = <T,>(promise: Promise<T>, ms: number): Promise<T> => {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`Request exceeded hard timeout of ${ms}ms`)), ms);
    promise.then(
      (value) => { clearTimeout(timer); resolve(value); },
      (err) => { clearTimeout(timer); reject(err); }
    );
  });
};

class SecurSpaceApp extends Component<object, IAppState> {
  private sessionGeneration = 0;
  private verificationRequest?: Promise<Account | null>;
  private verificationTimer?: ReturnType<typeof setTimeout>;
  private stopNotifications?: () => void;
  private mounted = false;

  constructor(props: any) {
    super(props);

    TagManager.initialize({
      gtmId: 'GTM-M7M8FTW'
    });

    this.state = {
      user: null,
      loadingUserDetails: true,
    };
  }

  componentDidMount(): void {
    this.mounted = true;
    window.addEventListener('focus', this.checkVerification);
    window.addEventListener('online', this.checkVerification);
    document.addEventListener('visibilitychange', this.checkVerification);
    this.stopNotifications = listenForVerification(this.refreshAccountVerification);
    const generation = this.sessionGeneration;
    if (!this.state.user || !this.state.user.username) {
      Busy.set(true);
      withHardTimeout(requestLoggedInAuthoritySource(), INITIAL_AUTHORITY_TIMEOUT_MS)
        .then((resp: {body: any}) => {
          if (this.mounted && generation === this.sessionGeneration) {
            this.handleAlreadyLoggedIn(resp.body);
          } else {
            // Session changed (or component unmounted) while the initial authority
            // check was in flight. Its result is stale and must be discarded, but the
            // loading state and busy overlay still need to be cleared so the app never
            // gets stuck on the full-page spinner.
            this.clearInitialLoadIndicators();
          }
        })
        .catch((err: any) => {
          if (this.mounted && generation === this.sessionGeneration) {
            this.handleReloadFailure(err);
          } else {
            this.clearInitialLoadIndicators();
          }
        });
    } else {
      this.setState({ loadingUserDetails: false });
    }

    const fancyboxElement = $(".fancybox");

    if (fancyboxElement && fancyboxElement.fancybox) {
      fancyboxElement.fancybox({
        padding: 0,
        arrows: true,
        nextClick: true,
        autoPlay: false,
        playSpeed: 1500,
        openEffect: "elastic",
        openSpeed: "slow",
        closeEffect: "fade",
        closeSpeed: "fast",
        nextEffect: "elastic",
        nextSpeed: "slow",
        closeBtn: true,
      });
    }
  }

  componentDidUpdate(): void {
    this.scheduleVerification();
  }

  componentWillUnmount(): void {
    this.mounted = false;
    ++this.sessionGeneration;
    clearTimeout(this.verificationTimer);
    window.removeEventListener('focus', this.checkVerification);
    window.removeEventListener('online', this.checkVerification);
    document.removeEventListener('visibilitychange', this.checkVerification);
    this.stopNotifications?.();
    SessionPoller.cancelPolling();
  }

  private scheduleVerification = (): void => {
    clearTimeout(this.verificationTimer);
    if (isAuthenticatedAuthority(this.state.user) &&
        document.visibilityState === 'visible' && navigator.onLine) {
      this.verificationTimer = setTimeout(this.checkVerification, this.state.user.emailVerified ? 60000 : 30000);
    }
  };

  private checkVerification = (): void => {
    this.scheduleVerification();
    if (document.visibilityState === 'visible') this.refreshAccountVerification();
  };

  private refreshAccountVerification = (): void => {
    if (isAuthenticatedAuthority(this.state.user) && navigator.onLine) {
      void this.refreshVerification().catch(() => { /* Retry without discarding work. */ });
    }
  };

  refreshVerification = (): Promise<Account | null> => {
    if (this.verificationRequest) return this.verificationRequest;
    const generation = this.sessionGeneration;
    const active = this.state.user;
    const isCurrent = () => this.mounted && generation === this.sessionGeneration;
    const pending = withHardTimeout(requestLoggedInAuthoritySource(), INITIAL_AUTHORITY_TIMEOUT_MS).then(({ body }) => {
      if (!isCurrent()) throw new Error('Session changed during verification refresh');
      if (body?.username === 'anonymousUser') {
        if (isAuthenticatedAuthority(active)) this.handlePollingFail();
        return null;
      }
      if (!isAuthenticatedAuthority(body) || typeof body.emailVerified !== 'boolean') {
        throw new Error('Unable to confirm the current account');
      }
      if (isAuthenticatedAuthority(active)) {
        if (body.id !== active.id || body.loggedInUsername !== active.loggedInUsername ||
            body.authorityId !== active.authorityId) {
          throw new Error('Account changed during verification refresh');
        }
        this.setState(state => isCurrent() && state.user?.id === active.id &&
          state.user.loggedInUsername === active.loggedInUsername &&
          (state.user.emailVerified !== body.emailVerified || state.user.email !== body.email || state.user.pendingEmail !== body.pendingEmail)
          ? { user: { ...state.user, emailVerified: body.emailVerified, email: body.email, pendingEmail: body.pendingEmail } } : null);
      } else {
        this.handleAlreadyLoggedIn(body);
      }
      return body;
    }).catch(error => {
      if (isCurrent() && isSessionExpired(error)) {
        if (isAuthenticatedAuthority(active)) this.handlePollingFail();
        return null;
      }
      throw error;
    }).finally(() => {
      if (this.verificationRequest === pending) this.verificationRequest = undefined;
    });
    this.verificationRequest = pending;
    return pending;
  };

  /**
   * Guarantees the initial "loading user details" state and the full-page Busy
   * overlay always get cleared, even when the initial /api/authority response is
   * discarded as stale (component unmounted or session changed mid-flight). Without
   * this, the app could be left showing the busy spinner forever.
   */
  private clearInitialLoadIndicators = (): void => {
    if (this.mounted) {
      this.setState({ loadingUserDetails: false });
    }
    Busy.set(false);
  };

  handleAlreadyLoggedIn = (account: Account): void => {
    ++this.sessionGeneration;
    this.setState({
      user: isAuthenticatedAuthority(account) ? account : null,
      loadingUserDetails: false,
    });
    if (isAuthenticatedAuthority(account)) {
      SessionPoller.beginPolling(this.handlePollingFail);
    }
    Busy.set(false);
  };

  handleReloadFailure = (err: any): void => {
    if (isSessionExpired(err) && window.location.href !== window.location.origin &&
      !LoginRedirectPathnameBlockList.includes(window.location.pathname) &&
      !(
      LoginRedirectNestedPathnameBlockList.some((blockedPath: string) => window.location.pathname.startsWith(blockedPath)) &&
      !LoginRedirectedNestedPathnameExplicitlyAllowedList.includes(window.location.pathname)
      )
    ) {
      window.location.href = `${LOGIN_PATH}?redirect=${encodeURI(window.location.pathname + window.location.search)}`;
    }
    this.setState({ loadingUserDetails: false });
    Busy.set(false);
  };

  updateUser = (user: Account, history?: NavigateFunction, redirectPath?: string, state?: any): void => {
    ++this.sessionGeneration;
    this.verificationRequest = undefined;
    this.setState({ user: isAuthenticatedAuthority(user) ? user : null });
    if (isAuthenticatedAuthority(user)) {
      SessionPoller.beginPolling(this.handlePollingFail);
    } else {
      SessionPoller.cancelPolling();
    }

    if (history) {
      const path = redirectPath || getLandingRedirectPathForUser(user);
      if (state) {
        history(path, { state: state });
      } else {
        history(path);
      }
    }
  };

  handlePollingFail = (): void => {
    this.clearUser();
    if (window.location.pathname === '/verify') return;
    /*
      It's not worth re-rendering the entire tree every time
      the url changes to provide react router's history object
      here, so we are just going to do redirect the old fashioned
      way
     */
    window.location.href = `${LOGIN_PATH}?timeout=true`;
  };

  clearUser = (): void => {
    ++this.sessionGeneration;
    this.verificationRequest = undefined;
    clearTimeout(this.verificationTimer);
    this.setState({ user: null });
    SessionPoller.cancelPolling();
  };

  logout = (): void => {
    this.clearUser();
    window.location.href = "/logout";
  };

  generateAppContext = (): IAppContext => {
    return {
      user: this.state.user,
      loadingUserDetails: this.state.loadingUserDetails,
      updateUser: this.updateUser,
      clearUser: this.clearUser,
      logout: this.logout,
      refreshVerification: this.refreshVerification,
      observableMap: seedObservableMap(new KeyValueObservable<ObservablePayloadType>())
    };
  };

  render(): JSX.Element {
    return (
      <AppContext.Provider value={this.generateAppContext()}>
        <LDIdentityProvider user={this.state.user ?? null}>
          <FaroProvider user={this.state.user ?? null}>
            <PendoProvider user={this.state.user ?? null}>
              <GeocoderProvider>
                <HelmetProvider>
                  {/* index.html deliberately carries no static <title> or og:* tags.
                      HeadDefaults renders the site-wide set only while no page SEO
                      component has claimed the head — react-helmet-async 3 on React 19
                      does not de-duplicate across <Helmet> instances. */}
                  <HeadClaimProvider>
                    <HeadDefaults />
                  <ThemeProvider theme={globalThemes}>
                    <CssBaseline />
                    <SnackbarProvider>
                      <SecurSpaceRouter />
                    </SnackbarProvider>
                  </ThemeProvider>
                  </HeadClaimProvider>
                </HelmetProvider>
              </GeocoderProvider>
            </PendoProvider>
          </FaroProvider>
        </LDIdentityProvider>
      </AppContext.Provider>
    );
  }
}

export default SecurSpaceApp;
